import { computed, onScopeDispose, ref } from 'vue';
import { defineStore } from 'pinia';
import type { ServiceStatusView } from 'shared';
import { fetchServiceStatus } from '@/api/service-status.api';
import { useCollaborationSocket } from '@/composables/useCollaborationSocket';

const NORMAL_POLL_MS = 60_000;
const URGENT_POLL_MS = 15_000;

export const useServiceStatusStore = defineStore('service-status', () => {
  const status = ref<ServiceStatusView | null>(null);
  const unavailable = ref(false);
  const refreshing = ref(false);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopRealtime: (() => void) | null = null;
  let started = false;

  const hasBanner = computed(() => status.value !== null || unavailable.value);
  const interval = computed(() =>
    unavailable.value || status.value?.phase === 'active' ? URGENT_POLL_MS : NORMAL_POLL_MS,
  );

  function clearTimer(): void {
    if (timer) clearTimeout(timer);
    timer = null;
  }
  function schedule(): void {
    clearTimer();
    if (typeof document !== 'undefined' && document.hidden) return;
    timer = setTimeout(() => void refresh(), interval.value);
  }
  async function refresh(): Promise<void> {
    if (refreshing.value) return;
    refreshing.value = true;
    try {
      const next = await fetchServiceStatus();
      status.value = next.phase === 'normal' ? null : next;
      unavailable.value = false;
    } catch {
      unavailable.value = true;
    } finally {
      refreshing.value = false;
      schedule();
    }
  }
  function onVisible(): void {
    if (!document.hidden) void refresh();
  }
  function start(): void {
    if (typeof window === 'undefined' || started) return;
    started = true;
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    stopRealtime = useCollaborationSocket().onRealtimeEvent((event) => {
      if (event.type === 'system.service-status.changed.v1') void refresh();
    });
    void refresh();
  }
  function stop(): void {
    started = false;
    clearTimer();
    stopRealtime?.();
    stopRealtime = null;
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', refresh);
    window.removeEventListener('focus', refresh);
    document.removeEventListener('visibilitychange', onVisible);
  }
  onScopeDispose(stop);
  return { status, unavailable, refreshing, hasBanner, refresh, start, stop };
});
